// Top-level `(dashboard …)` op.
//
// The ONLY dashboard op registered in the engine's `opMeta` (hence the
// only one callable at script top level). It dispatches its positional
// tile children through `TILE_TABLE` (context-restricted to
// metric / cartesian / table) and wraps the assembled tiles in the
// lone new `DashboardSpec` Value (V-shape — `ColumnRef`s unresolved,
// no I/O yet). Tiles and sub-forms are never in `opMeta`; they live in
// `dashboardMeta` and are reachable only through this hierarchy.
//
// One kwarg — mandatory `:title` (1–120 chars), rendered in the
// dashboard header. The 12-column grid is fixed; tile layout is fully
// determined by declaration order + each tile's `:span [W H]`.

import { z } from 'zod';

import type { EvalResult } from '../../env.ts';
import type { AstInput } from '../../normalize.ts';
import {
  createOpTable,
  defineScope,
  type NameTable,
  type OpNameOf,
  opMeta,
  opNameOf,
  type Scope,
  type SpecialCtx,
  type SpecialOpMeta,
} from '../../op-meta.ts';
import { paramPath } from '../../path.ts';
import { astArg } from '../../schema/ast-codec.ts';
import { DashboardSpec, type Value } from '../../value.ts';
import { type DashboardCtx, dashboardMeta, dispatchDashboardChild } from './dispatch.ts';
import { cartesian } from './tiles/cartesian.ts';
import { donut } from './tiles/donut.ts';
import { markdown } from './tiles/markdown.ts';
import { metric } from './tiles/metric.ts';
import { table } from './tiles/table.ts';
import { treemap } from './tiles/treemap.ts';
import type { TileSpec } from './value.ts';

/** Tile ops legal as positional children of `(dashboard …)`. */
const TILE_SCHEMAS = [metric, cartesian, table, donut, treemap, markdown] as const;
// Compile-time SSOT guard: the registered tile ops (`TILE_SCHEMAS`) and the
// `TileSpec` union must stay in lockstep. A `TileSpec` variant with no tile op
// here — or a tile op with no matching spec — collapses the type to `never`,
// so `const … = true` fails the build, keeping the dispatch table from
// silently drifting out of sync with the renderer's spec set.
type RegisteredTileName = OpNameOf<(typeof TILE_SCHEMAS)[number]>;
type TileSpecName = TileSpec['kind'] extends `${infer NameType}-spec` ? NameType : never;
const _tileRegistryInSync: [RegisteredTileName] extends [TileSpecName]
  ? [TileSpecName] extends [RegisteredTileName]
    ? true
    : never
  : never = true;

/** Doc + dispatch scope for the tile context. */
export const tileScope: Scope<SpecialOpMeta> = defineScope({
  name: 'Dashboard tiles',
  description:
    'Positional children of `(dashboard …)`. Each carries `:span [W H]` — [width height], each component 1–12 columns of the fixed 12-column grid.',
  registry: dashboardMeta,
  schemas: TILE_SCHEMAS,
});

const TILE_TABLE: NameTable = createOpTable(TILE_SCHEMAS);

export const dashboard = z
  .object({
    name: z.literal('dashboard'),
    params: z.array(astArg('<tile>')).min(1),
    // No `.describe()`: the op description already documents `:title` —
    // a kwarg row would repeat it verbatim.
    title: z.string().min(1).max(120),
  })
  .describe(
    `Assemble a dashboard from tiles (${TILE_SCHEMAS.map(opNameOf).join(', ')}). ` +
      'Mandatory `:title` (1–120 chars) is rendered in the dashboard header. ' +
      'Fixed 12-column grid; tiles flow left-to-right, top-to-bottom in declaration order; ' +
      'each tile carries `:span [W H]`. Returns a dashboard spec rendered by the host.',
  )
  .register(opMeta, {
    evaluation: 'special',
    effect: 'pure',
    childScopes: [tileScope],
    example: {
      expr: '(dashboard :title "Wells overview" (metric :span [4 2] :value (col w v) :label "X" :format compact) (table (column :ref (col w r) :label "R" :format text) :span [12 4]))',
    },
    apply: async (data, ctx: SpecialCtx): Promise<EvalResult> => {
      const baseCtx: DashboardCtx = {
        env: ctx.env,
        spans: ctx.spans,
        path: ctx.path,
        evalCtx: ctx.evalCtx,
        evalNode: ctx.evalNode,
      };
      const tiles = await Promise.all(
        data.params.map((node, i) =>
          dispatchDashboardChild(node as AstInput, TILE_TABLE, baseCtx, paramPath(ctx.path, i)),
        ),
      );
      // `TILE_TABLE` only resolves metric/cartesian/table, so every
      // dispatched child is a `TileSpec` by construction.
      return { value: new DashboardSpec(data.title, tiles as readonly TileSpec[]) satisfies Value, env: ctx.env };
    },
  });
